import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { PlusCircle } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import AddTenantDialog from './AddTenantDialog';

interface Tenant {
  id: string;
  tenant_name: string;
  microsoft_tenant_id: string;
  created_at: string;
}

interface CustomerTenantsTabProps {
  customerId: string;
}

const CustomerTenantsTab: React.FC<CustomerTenantsTabProps> = ({ customerId }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddTenantDialogOpen, setIsAddTenantDialogOpen] = useState(false);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('customer_microsoft_tenants')
        .select('id, tenant_name, microsoft_tenant_id, created_at')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTenants(data || []);
    } catch (error) {
      console.error('Erro ao buscar tenants:', error);
      toast.error('Não foi possível carregar os tenants.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchTenants();
    }
  }, [customerId]);

  const handleTenantAdded = () => {
    fetchTenants(); // Refresh the list
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Microsoft Tenants</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddTenantDialogOpen(true)}
            className="gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            Adicionar Tenant
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum tenant encontrado para este cliente.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome do Tenant</TableHead>
                    <TableHead>Microsoft Tenant ID</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell className="font-medium">{tenant.tenant_name}</TableCell>
                      <TableCell>{tenant.microsoft_tenant_id}</TableCell>
                      <TableCell>{formatDate(tenant.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      <AddTenantDialog
        customerId={customerId}
        isOpen={isAddTenantDialogOpen}
        onClose={() => setIsAddTenantDialogOpen(false)}
        onTenantAdded={handleTenantAdded}
      />
    </>
  );
};

export default CustomerTenantsTab;
